import React, { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import {
  Download, FileText, Image as ImageIcon, Moon, Sun, Type, MessageSquare,
  Sparkles, Palette, Users, Shuffle, ChevronDown, Bookmark, Trash2,
} from 'lucide-react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { A4Poster, A4PosterHandle, A4PosterContent } from './A4Poster';
import { canvasToPdfBlob } from '../../lib/posterPdf';
import {
  POSTER_FONTS,
  POSTER_MESSAGES,
  POSTER_BACKGROUNDS,
  POSTER_ORNAMENTS,
  POSTER_MASCOTS,
  POSTER_MASCOT_POSITIONS,
  BACKGROUND_PRESETS,
  MASCOT_SIZES,
  PATTERN_KNOBS,
  ORNAMENT_COUNT,
  DEFAULT_POSTER_MESSAGE,
  DEFAULT_FONT_CHOICES,
  DEFAULT_POSTER_BACKGROUND,
  DEFAULT_POSTER_ORNAMENTS,
  DEFAULT_POSTER_MASCOTS,
  DEFAULT_MASCOT_POSITION,
  DEFAULT_MASCOT_SIZE,
  DEFAULT_BG_PRIMARY,
  DEFAULT_BG_SECONDARY,
  FONT_SLOTS,
  defaultPatternSettings,
  getBackgroundDef,
  PosterFontChoices,
  PosterBackground,
  PosterOrnaments,
  PosterMascots,
  PosterMascotPosition,
  PosterMascotSize,
  PatternKnob,
  PatternSettings,
} from '../../lib/posterDesign';
import {
  PosterDesignConfig,
  PosterPreset,
  listPresets,
  savePreset,
  deletePreset,
  sanitizeConfig,
} from '../../lib/posterPresets';
import { getPortalUrl } from '../../lib/portal';
import { clayTapScale } from '../../constants/animations';
import { track, EVENTS } from '../../lib/mixpanel';
import styles from './A4PosterModal.module.css';

// ── Collapsible control group ─────────────────────────────────────────────────

function Group({
  icon: Icon,
  title,
  summary,
  defaultOpen = false,
  children,
}: {
  icon: React.ElementType;
  title: string;
  summary?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={styles['group']}>
      <button
        type="button"
        className={styles['group-toggle']}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Icon className={styles['icon-sm']} />
        <span className={styles['group-title']}>{title}</span>
        {summary && !open && <span className={styles['group-summary']}>{summary}</span>}
        <ChevronDown
          className={[styles['icon-sm'], styles['chevron'], open ? styles['chevron-open'] : '']
            .filter(Boolean).join(' ')}
        />
      </button>
      {open && <div className={styles['group-body']}>{children}</div>}
    </section>
  );
}

function Slider({
  label, value, min, max, step, unit, onChange,
}: {
  label: string;
  value: number;
  min: number; max: number; step: number;
  unit?: string;
  onChange: (v: number) => void;
}) {
  return (
    <label className={styles['field']}>
      <span className={styles['field-label']}>
        {label}
        <span className={styles['field-value']}>
          {Number.isInteger(step) ? value : value.toFixed(2)}{unit ?? ''}
        </span>
      </span>
      <input
        type="range"
        className={styles['range']}
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
    </label>
  );
}

export interface A4PosterDesignerProps {
  albumId?: string;
  albumName: string;
  eventName: string;
  inviteToken: string;
}

/**
 * A4PosterDesigner — the poster customiser and export controls, without any
 * modal chrome, so it can be hosted either in its own modal or as a tab
 * inside the album share modal.
 *
 * Everything renders client-side on a canvas: the preview draws at ~150 DPI
 * and exports re-draw the same layout at 300 DPI for print.
 */
export function A4PosterDesigner({
  albumId,
  albumName,
  eventName,
  inviteToken,
}: A4PosterDesignerProps) {
  const posterRef = useRef<A4PosterHandle>(null);

  const [bgPrimary, setBgPrimary] = useState(DEFAULT_BG_PRIMARY);
  const [bgSecondary, setBgSecondary] = useState(DEFAULT_BG_SECONDARY);
  const [background, setBackground] = useState<PosterBackground>(DEFAULT_POSTER_BACKGROUND);
  const [patternSettings, setPatternSettings] = useState<PatternSettings>(
    () => defaultPatternSettings(DEFAULT_POSTER_BACKGROUND),
  );

  const [ornaments, setOrnaments] = useState<PosterOrnaments>(DEFAULT_POSTER_ORNAMENTS);
  const [ornamentCount, setOrnamentCount] = useState(ORNAMENT_COUNT.default);
  const [ornamentSeed, setOrnamentSeed] = useState(1337);

  const [mascots, setMascots] = useState<PosterMascots>(DEFAULT_POSTER_MASCOTS);
  const [mascotPosition, setMascotPosition] =
    useState<PosterMascotPosition>(DEFAULT_MASCOT_POSITION);
  const [mascotSize, setMascotSize] = useState<PosterMascotSize>(DEFAULT_MASCOT_SIZE);

  const [fonts, setFonts] = useState<PosterFontChoices>(DEFAULT_FONT_CHOICES);
  const [message, setMessage] = useState(DEFAULT_POSTER_MESSAGE);
  const [showUrl, setShowUrl] = useState(true);
  const [exporting, setExporting] = useState<'png' | 'pdf' | null>(null);

  const [presets, setPresets] = useState<PosterPreset[]>(() => listPresets());
  const [presetId, setPresetId] = useState('');
  const [presetName, setPresetName] = useState('');

  const inviteUrl = `${getPortalUrl('viewer')}/join/${inviteToken}`;
  const backgroundDef = getBackgroundDef(background);

  // The design half of the state — what a preset stores and restores.
  const designConfig = useMemo<PosterDesignConfig>(
    () => ({
      message, showUrl, fonts,
      bgPrimary, bgSecondary, background, patternSettings,
      ornaments, ornamentCount, ornamentSeed,
      mascots, mascotPosition, mascotSize,
    }),
    [
      message, showUrl, fonts,
      bgPrimary, bgSecondary, background, patternSettings,
      ornaments, ornamentCount, ornamentSeed,
      mascots, mascotPosition, mascotSize,
    ],
  );

  const applyConfig = (config: PosterDesignConfig) => {
    setMessage(config.message);
    setShowUrl(config.showUrl);
    setFonts(config.fonts);
    setBgPrimary(config.bgPrimary);
    setBgSecondary(config.bgSecondary);
    setBackground(config.background);
    setPatternSettings(config.patternSettings);
    setOrnaments(config.ornaments);
    setOrnamentCount(config.ornamentCount);
    setOrnamentSeed(config.ornamentSeed);
    setMascots(config.mascots);
    setMascotPosition(config.mascotPosition);
    setMascotSize(config.mascotSize);
  };

  const handleLoadPreset = (id: string) => {
    setPresetId(id);
    const preset = presets.find((p) => p.id === id);
    if (!preset) return;
    applyConfig(sanitizeConfig(preset.config, designConfig));
    setPresetName(preset.name);
    toast.success(`Loaded “${preset.name}”`);
  };

  const handleSavePreset = () => {
    const saved = savePreset(presetName, designConfig);
    if (!saved) {
      toast.error(presetName.trim() ? 'Could not save — storage is unavailable' : 'Name the preset first');
      return;
    }
    setPresets(listPresets());
    setPresetId(saved.id);
    toast.success(`Saved “${saved.name}”`);
  };

  const handleDeletePreset = () => {
    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;
    deletePreset(preset.id);
    setPresets(listPresets());
    setPresetId('');
    toast.success(`Deleted “${preset.name}”`);
  };

  // Identity matters: A4Poster redraws whenever `content` changes.
  const content = useMemo<A4PosterContent>(
    () => ({
      eventName, albumName, inviteUrl, message, showUrl, fonts,
      bgPrimary, bgSecondary, background, patternSettings,
      ornaments, ornamentCount, ornamentSeed,
      mascots, mascotPosition, mascotSize,
    }),
    [
      eventName, albumName, inviteUrl, message, showUrl, fonts,
      bgPrimary, bgSecondary, background, patternSettings,
      ornaments, ornamentCount, ornamentSeed,
      mascots, mascotPosition, mascotSize,
    ],
  );

  const fileBase = useMemo(() => {
    const source = albumName || eventName || 'pixel-machha';
    return (
      source
        .replace(/[^a-zA-Z0-9\s-_]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .toLowerCase() || 'poster'
    );
  }, [albumName, eventName]);

  // Switching pattern resets its knobs — a spacing that suited stripes is
  // rarely right for confetti.
  const changeBackground = (key: PosterBackground) => {
    setBackground(key);
    setPatternSettings(defaultPatternSettings(key));
  };

  const setKnob = (knob: PatternKnob, value: number) =>
    setPatternSettings((prev) => ({ ...prev, [knob]: value }));

  const handleExport = async (format: 'png' | 'pdf') => {
    if (exporting) return;
    setExporting(format);
    try {
      const canvas = await posterRef.current?.renderPrintCanvas();
      if (!canvas) throw new Error('Poster is not ready yet');

      const blob =
        format === 'pdf'
          ? await canvasToPdfBlob(canvas)
          : await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Could not generate the poster file');

      downloadBlob(blob, `${fileBase}-poster.${format}`);
      track(EVENTS.ALBUM_POSTER_DOWNLOADED, {
        album_id: albumId,
        format,
        background,
        ornaments,
        ornament_count: ornamentCount,
        mascots,
        mascot_position: mascotPosition,
        mascot_size: mascotSize,
        bg_primary: bgPrimary,
        bg_secondary: bgSecondary,
        message_is_custom: !POSTER_MESSAGES.includes(message),
        fonts: Object.values(fonts).join(','),
      });
      toast.success(`Poster downloaded as ${format.toUpperCase()}`);
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'Download failed';
      toast.error(reason);
    } finally {
      setExporting(null);
    }
  };

  const isPreset = POSTER_MESSAGES.includes(message);
  const activePreset = BACKGROUND_PRESETS.find(
    (b) => b.primary === bgPrimary && b.secondary === bgSecondary,
  );

  return (
    <div className={styles['layout']}>
      {/* ── Controls ─────────────────────────────────────────────────── */}
      <div className={styles['controls']}>
        <Group
          icon={Bookmark}
          title="Presets"
          summary={presets.length ? `${presets.length} saved` : 'None saved'}
        >
          <label className={styles['field']}>
            <span className={styles['field-label']}>Saved designs</span>
            <select
              className={styles['select']}
              value={presetId}
              disabled={presets.length === 0}
              onChange={(e) => handleLoadPreset(e.target.value)}
            >
              <option value="">
                {presets.length ? 'Choose a preset…' : 'No saved presets yet'}
              </option>
              {presets.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>

          <label className={styles['field']}>
            <span className={styles['field-label']}>Save current design as</span>
            <input
              type="text"
              className={styles['input']}
              maxLength={60}
              placeholder="e.g. Wedding — dark"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
            />
          </label>

          <div className={styles['preset-actions']}>
            <button
              type="button"
              className={styles['shuffle-btn']}
              onClick={handleSavePreset}
              disabled={!presetName.trim()}
            >
              <Bookmark className={styles['icon-sm']} />
              Save preset
            </button>
            <button
              type="button"
              className={styles['delete-btn']}
              onClick={handleDeletePreset}
              disabled={!presetId}
              title="Delete the selected preset"
              aria-label="Delete the selected preset"
            >
              <Trash2 className={styles['icon-sm']} />
            </button>
          </div>
          <p className={styles['hint']}>
            Presets store the design only, so they can be applied to any album. They are saved in
            this browser and won&apos;t follow you to another device.
          </p>
        </Group>

        <Group
          icon={Palette}
          title="Background"
          summary={`${activePreset?.label ?? 'Custom'} · ${backgroundDef.label}`}
          defaultOpen
        >
          <div className={styles['swatch-row']}>
            {BACKGROUND_PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                title={preset.label}
                aria-label={preset.label}
                aria-pressed={activePreset?.key === preset.key}
                onClick={() => {
                  setBgPrimary(preset.primary);
                  setBgSecondary(preset.secondary);
                }}
                className={[
                  styles['swatch'],
                  activePreset?.key === preset.key ? styles['swatch-active'] : '',
                ].filter(Boolean).join(' ')}
                style={{
                  background: `linear-gradient(135deg, ${preset.primary}, ${preset.secondary})`,
                }}
              />
            ))}
          </div>

          <div className={styles['color-row']}>
            <label className={styles['color-field']}>
              <span className={styles['field-label']}>Primary</span>
              <input
                type="color"
                className={styles['color-input']}
                value={bgPrimary}
                onChange={(e) => setBgPrimary(e.target.value)}
              />
            </label>
            <label className={styles['color-field']}>
              <span className={styles['field-label']}>Secondary</span>
              <input
                type="color"
                className={styles['color-input']}
                value={bgSecondary}
                onChange={(e) => setBgSecondary(e.target.value)}
              />
            </label>
            <div className={styles['color-field']}>
              <span className={styles['field-label']}>Quick set</span>
              <div className={styles['mode-row']}>
                {([
                  { label: 'Light', Icon: Sun, p: '#fffdfd', s: '#f4e8e8' },
                  { label: 'Dark', Icon: Moon, p: '#1d1416', s: '#0a090b' },
                ]).map(({ label, Icon, p, s }) => (
                  <button
                    key={label}
                    type="button"
                    className={styles['mode-btn']}
                    onClick={() => { setBgPrimary(p); setBgSecondary(s); }}
                  >
                    <Icon className={styles['icon-sm']} />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className={styles['hint']}>
            Text and surface colours follow the background&apos;s brightness automatically, so a
            dark pair flips the whole poster to light type.
          </p>

          <label className={styles['field']}>
            <span className={styles['field-label']}>Pattern</span>
            <select
              className={styles['select']}
              value={background}
              onChange={(e) => changeBackground(e.target.value as PosterBackground)}
            >
              {POSTER_BACKGROUNDS.map((bg) => (
                <option key={bg.key} value={bg.key}>{bg.label}</option>
              ))}
            </select>
          </label>

          {backgroundDef.knobs.map((knob) => {
            const meta = PATTERN_KNOBS[knob];
            return (
              <Slider
                key={knob}
                label={meta.label}
                unit={meta.unit}
                min={meta.min}
                max={meta.max}
                step={meta.step}
                value={patternSettings[knob]}
                onChange={(v) => setKnob(knob, v)}
              />
            );
          })}
          {backgroundDef.knobs.length === 0 && (
            <p className={styles['hint']}>This background has no extra options.</p>
          )}
        </Group>

        <Group
          icon={Sparkles}
          title="Clay ornaments"
          summary={`${POSTER_ORNAMENTS.find((o) => o.key === ornaments)?.label} · ${ornamentCount}`}
        >
          <label className={styles['field']}>
            <span className={styles['field-label']}>Shape</span>
            <select
              className={styles['select']}
              value={ornaments}
              onChange={(e) => setOrnaments(e.target.value as PosterOrnaments)}
            >
              {POSTER_ORNAMENTS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </label>
          <Slider
            label="Quantity"
            min={ORNAMENT_COUNT.min}
            max={ORNAMENT_COUNT.max}
            step={ORNAMENT_COUNT.step}
            value={ornamentCount}
            onChange={setOrnamentCount}
          />
          <button
            type="button"
            className={styles['shuffle-btn']}
            onClick={() => setOrnamentSeed(Math.floor(Math.random() * 100000) + 1)}
            disabled={ornaments === 'none' || ornamentCount === 0}
          >
            <Shuffle className={styles['icon-sm']} />
            Randomise positions
          </button>
        </Group>

        <Group
          icon={Users}
          title="Mascots"
          summary={POSTER_MASCOTS.find((m) => m.key === mascots)?.label}
        >
          <label className={styles['field']}>
            <span className={styles['field-label']}>Which</span>
            <select
              className={styles['select']}
              value={mascots}
              onChange={(e) => setMascots(e.target.value as PosterMascots)}
            >
              {POSTER_MASCOTS.map((m) => (
                <option key={m.key} value={m.key}>{m.label}</option>
              ))}
            </select>
          </label>
          <label className={styles['field']}>
            <span className={styles['field-label']}>Position</span>
            <select
              className={styles['select']}
              value={mascotPosition}
              disabled={mascots === 'none'}
              onChange={(e) => setMascotPosition(e.target.value as PosterMascotPosition)}
            >
              {POSTER_MASCOT_POSITIONS.map((m) => (
                <option key={m.key} value={m.key}>{m.label}</option>
              ))}
            </select>
          </label>
          <label className={styles['field']}>
            <span className={styles['field-label']}>Size</span>
            <select
              className={styles['select']}
              value={mascotSize}
              disabled={mascots === 'none'}
              onChange={(e) => setMascotSize(e.target.value as PosterMascotSize)}
            >
              {MASCOT_SIZES.map((m) => (
                <option key={m.key} value={m.key}>{m.label}</option>
              ))}
            </select>
          </label>
          <p className={styles['hint']}>
            Dropping the mascots gives the QR code the extra height instead.
          </p>
        </Group>

        <Group icon={Type} title="Typography" summary={POSTER_FONTS.find((f) => f.key === fonts.eventName)?.label}>
          {FONT_SLOTS.map((slot) => (
            <label key={slot.key} className={styles['field']}>
              <span className={styles['field-label']}>{slot.label}</span>
              <select
                className={styles['select']}
                value={fonts[slot.key]}
                onChange={(e) => setFonts((prev) => ({ ...prev, [slot.key]: e.target.value }))}
              >
                {POSTER_FONTS.map((font) => (
                  <option key={font.key} value={font.key}>{font.label}</option>
                ))}
              </select>
            </label>
          ))}
          <p className={styles['hint']}>
            The invite link always prints in monospace so guests can type it accurately.
          </p>
        </Group>

        <Group icon={MessageSquare} title="Message &amp; link" summary={message}>
          <label className={styles['field']}>
            <span className={styles['field-label']}>Choose a message</span>
            <select
              className={styles['select']}
              value={isPreset ? message : '__custom__'}
              onChange={(e) => {
                if (e.target.value !== '__custom__') setMessage(e.target.value);
              }}
            >
              {POSTER_MESSAGES.map((preset) => (
                <option key={preset} value={preset}>{preset}</option>
              ))}
              <option value="__custom__">Custom message…</option>
            </select>
          </label>
          <label className={styles['field']}>
            <span className={styles['field-label']}>Or write your own</span>
            <input
              type="text"
              className={styles['input']}
              maxLength={90}
              value={message}
              placeholder={DEFAULT_POSTER_MESSAGE}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>
          <label className={styles['check-row']}>
            <input
              type="checkbox"
              className={styles['checkbox']}
              checked={showUrl}
              onChange={(e) => setShowUrl(e.target.checked)}
            />
            <span className={styles['check-text']}>
              Print the invite link under the QR code
            </span>
          </label>
          <p className={styles['hint']}>
            The link is the fallback when a scan fails — hiding it leaves guests no way in if
            their camera won&apos;t read the code.
          </p>
        </Group>

        {/* Export stays outside the accordion — it's the point of the screen. */}
        <section className={styles['export-section']}>
          <h3 className={styles['group-label']}>
            <Download className={styles['icon-sm']} />
            Download
          </h3>
          <div className={styles['export-row']}>
            <motion.div whileTap={clayTapScale} className={styles['export-btn-wrap']}>
              <Button
                variant="primary"
                loading={exporting === 'png'}
                disabled={exporting !== null}
                onClick={() => handleExport('png')}
                fullWidth
              >
                <ImageIcon className={styles['icon-btn']} />
                PNG
              </Button>
            </motion.div>
            <motion.div whileTap={clayTapScale} className={styles['export-btn-wrap']}>
              <Button
                variant="secondary"
                loading={exporting === 'pdf'}
                disabled={exporting !== null}
                onClick={() => handleExport('pdf')}
                fullWidth
              >
                <FileText className={styles['icon-btn']} />
                PDF
              </Button>
            </motion.div>
          </div>
          <p className={styles['hint']}>
            Exports at 300 DPI (2480 × 3508 px) — print-ready at full A4 size.
          </p>
        </section>
      </div>

      {/* ── Preview ──────────────────────────────────────────────────── */}
      <div className={styles['preview']}>
        <div className={styles['preview-inner']}>
          <A4Poster ref={posterRef} content={content} />
        </div>
      </div>
    </div>
  );
}

/** Standalone modal wrapper around {@link A4PosterDesigner}. */
export function A4PosterModal({
  isOpen,
  onClose,
  ...designer
}: A4PosterDesignerProps & { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Guest Poster (A4)" size="full">
      <A4PosterDesigner {...designer} />
    </Modal>
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
